import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import type Descriptor from "../../features/rapidstor/entities/Descriptor";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import UnitType from "./UnitType";
import {
  distributeUnitsToGroups,
  UnitGroup,
} from "../../features/rapidstor/entities/UnitType";
import classes from "./Descriptor.module.scss";
import SmallDetail from "../SmallDetail";
import { Link } from "react-router-dom";
import { ReactComponent as IconRightChevron } from "../../assets/icon-right-chevron.svg";
import Location from "../../features/rapidstor/entities/Location";

interface Props extends React.HTMLAttributes<HTMLElement> {
  descriptor: Descriptor;
  recommended: boolean;
  onBookUnitClicked: () => void;
  onEmailQuoteClicked: () => void;
  onUnitTypeSelected: (descriptor: Descriptor, unitType: UnitType) => void;
  location: Location;
}

const groupLabels: Record<UnitGroup, string> = {
  upper: "Upper Level",
  ground: "Ground Floor",
  driveup: "Drive Up",
};

function Descriptor({
  onBookUnitClicked,
  onEmailQuoteClicked,
  onUnitTypeSelected,
  location,
  descriptor,
  recommended,
}: Props) {
  const unitGroups = useMemo(
    () => distributeUnitsToGroups(descriptor.unitTypes, location.rapidstor.groundFloor),
    [descriptor.unitTypes, location.rapidstor.groundFloor]
  );

  // Remove groups that don't have a unit type assigned to them
  const validGroups = useMemo(
    () =>
      Object.entries(unitGroups)
        .filter(([, type]) => type)
        .map(([group]) => group as UnitGroup)
        .sort(),
    [unitGroups]
  );

  const bestAvailableGroup = useMemo(
    () =>
      Object.entries(unitGroups)
        .filter(([, type]) => type)
        .sort(([, a], [, b]) => {
          if (a === null || b === null) {
            return -1;
          }
          if (!!a.totalAvailable === !!b.totalAvailable) {
            return a.stdRate - b.stdRate;
          }
          return a.totalAvailable ? -1 : 1;
        })
        .map(([group]) => group as UnitGroup)[0],
    [unitGroups]
  );

  const [group, setGroup] = useState<UnitGroup>(bestAvailableGroup ?? validGroups[0]);
  // The unitType variable will update when the group variable changes.
  const unitType = useMemo(() => unitGroups[group] as UnitType, [group, unitGroups]);

  const onChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    setGroup(event.currentTarget.value as UnitGroup);
  };

  useEffect(() => {
    onUnitTypeSelected(descriptor, unitType);
  }, [descriptor, unitType, onUnitTypeSelected]);

  return (
    <div className={classes.descriptor}>
      <Card
        className={classes.card}
        style={{ backgroundColor: "rgba(0,0,0,0.05)", border: "0", height: "100%" }}
      >
        <Card.Body className={classes.card_body}>
          <div className={"row"}>
            <div className={"col-sm-12 col-md-6"}>
              {unitGroups[group] && (
                <UnitType
                  onBookUnitClicked={onBookUnitClicked}
                  onEmailQuoteClicked={onEmailQuoteClicked}
                  descriptor={descriptor}
                  unitType={unitType}
                  recommended={recommended}
                />
              )}
            </div>

            <div className={"mt-2 col-sm-12 col-md-6"}>
              <SmallDetail bold className={"mb-1"}>
                Accessibility
              </SmallDetail>
              <div
                className={`${classes.groupSelection} d-flex column-gap-2 flex-wrap square-check`}
              >
                {validGroups.map((_group) => (
                  <button
                    key={_group}
                    onMouseDown={() => setGroup(_group)}
                    style={{
                      border: "none",
                      backgroundColor: "transparent",
                      color: "#000",
                    }}
                  >
                    <Form.Check
                      type={"checkbox"}
                      name={`position-${descriptor._id}`}
                      id={`position-${descriptor._id}-${_group}`}
                      label={groupLabels[_group]}
                      key={_group}
                      value={_group}
                      checked={group === _group}
                      onChange={onChangeEvent}
                    />
                  </button>
                ))}
              </div>
              <div className={classes.buttons}>
                <div className="d-flex gap-3 my-4">
                  {unitType.totalAvailable > 0 ? (
                    <button
                      type="button"
                      style={{ fontSize: 14 }}
                      onClick={onBookUnitClicked}
                      className="btn-md w-100 btn btn-primary"
                    >
                      Book Now
                    </button>
                  ) : (
                    <Link className="w-100" to={`/contact/${location.slug}`}>
                      <button
                        type="button"
                        style={{ fontSize: 14 }}
                        className="btn-md w-100 btn btn-primary"
                      >
                        Call For Availability
                      </button>
                    </Link>
                  )}
                  <button
                    type="button"
                    style={{ fontSize: 14 }}
                    onClick={onEmailQuoteClicked}
                    className="btn-md w-100 btn bg-white btn-outline-primary"
                  >
                    Email Quote
                  </button>
                </div>
              </div>
              <div className={classes.descriptor_description_desktop}>
                <SmallDetail bold>{descriptor.description}</SmallDetail>
                {unitType.deal && unitType.deal.descriptions.length > 0 && (
                  <ul className={`${classes.dotPoints} mt-2`}>
                    {unitType.deal.descriptions.map((desc, index) => (
                      <li key={index}>{desc}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </Card.Body>
        <IconRightChevron className={`${classes.arrow}`} />
      </Card>
      <div className={classes.descriptor_description_mobile}>
        <SmallDetail bold>{descriptor.description}</SmallDetail>
        {unitType.deal && unitType.deal.descriptions.length > 0 && (
          <ul className={`${classes.dotPoints} mt-2`}>
            {unitType.deal.descriptions.map((desc, index) => (
              <li key={index}>{desc}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default Descriptor;
